import { useAuth } from "@Auth/useAuth";
import { PurchaseOrderStatus } from "@Clients/PurchaseOrdersClient";
import { Footer } from "@common/SimpleDrawer/components/Footer";
import { Section } from "@common/SimpleDrawer/components/Section";
import { FormInput } from "@components/FormInput/FormInput";
import { FormAutocomplete } from "@components/FormAutocomplete/FormAutoComplete";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { FORM_ACTIONS } from "@models/FormActions";
import { Box, Button, IconButton, Typography } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useConfirmationModalStore } from "@stores/confirmationModalStore";
import { usePurchaseOrdersStore } from "@stores/purchaseOrdersStore";
import { useSuppliersStore } from "@stores/suppliersStore";
import { useProducts } from "@features/products/queries/useProducts";
import { useDrawer } from "@hooks/useDrawer";
import { onErrorShow } from "@utils/onErrorShow";
import { onInfoShow } from "@utils/onInfoShow";
import { useEffect, useReducer, useState } from "react";
import { CreateSupplierDrawer } from "./CreateSupplierDrawer";

interface AutocompleteOption {
  label: string;
  value: string;
}

type PurchaseOrderInitialState = {
  supplier_id: string | null;
  notes: string;
  status: PurchaseOrderStatus;
  purchase_order_items: PurchaseOrderItemForm[];
  errors: Record<string, string>;
  order_date: string;
  expected_delivery_date: string;
  currency: string;
};

type PurchaseOrderItemForm = {
  id: string;
  product_id: string;
  quantity_ordered: number;
  unit_price: number;
  notes?: string;
};

const initialState: PurchaseOrderInitialState = {
  supplier_id: null,
  notes: "",
  status: "draft",
  purchase_order_items: [],
  errors: {} as Record<string, string>,
  order_date: "",
  expected_delivery_date: "",
  currency: "USD",
};

interface Action<T> {
  type: FORM_ACTIONS;
  field?: keyof T;
  value?: T[keyof T];
  errors?: Record<string, string>;
}

function formReducer(
  state: PurchaseOrderInitialState,
  action: Action<PurchaseOrderInitialState>
) {
  switch (action.type) {
    case FORM_ACTIONS.SET_FIELD: {
      if (!action.field) return state;
      return {
        ...state,
        [action.field!]: action.value,
        errors: {
          ...state.errors,
          [action.field!]: "",
        },
      };
    }
    case FORM_ACTIONS.SET_ERRORS:
      return {
        ...state,
        errors: action.errors || {},
      };
    case FORM_ACTIONS.RESET:
      return initialState;
    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }
}

interface PurchaseOrderFormProps {
  handleClose: () => void;
}

const statusOptions = [
  { label: "Draft", value: "draft" },
  { label: "Pending", value: "pending" },
  { label: "Ordered", value: "ordered" },
  { label: "Partial", value: "partial" },
  { label: "Received", value: "received" },
  { label: "Cancelled", value: "cancelled" },
];

export const PurchaseOrderForm = ({ handleClose }: PurchaseOrderFormProps) => {
  const [state, dispatch] = useReducer(
    formReducer as React.Reducer<
      Partial<PurchaseOrderInitialState>,
      Action<PurchaseOrderInitialState>
    >,
    initialState
  );

  const { setTouch } = useConfirmationModalStore();
  const { baseUser } = useAuth();

  const {
    close: closeOnSubmit,
    context,
    selectedId,
  } = useDrawer();

  const { purchaseOrders, getPurchaseOrderById } = usePurchaseOrdersStore();
  const { data: products = [] } = useProducts();
  const { suppliers, getSuppliersByOrganizationId } = useSuppliersStore();
  const [openSupplierCreate, setOpenSupplierCreate] = useState(false);

  // Load suppliers on mount
  useEffect(() => {
    if (baseUser?.organization_id) {
      getSuppliersByOrganizationId(baseUser.organization_id, false);
    }
  }, [baseUser?.organization_id, getSuppliersByOrganizationId]);

  // Load existing purchase order details if editing
  useEffect(() => {
    if (context === DRAWER_CONTEXT.EDIT_PURCHASE_ORDER && selectedId) {
      getPurchaseOrderById(selectedId);
    }
  }, [context, selectedId, getPurchaseOrderById]);

  useEffect(() => {
    if (context === DRAWER_CONTEXT.EDIT_PURCHASE_ORDER && selectedId) {
      const purchaseOrder = purchaseOrders.find((po) => po.id === selectedId);
      if (purchaseOrder) {
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "supplier_id",
          value: purchaseOrder.supplier_id,
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "notes",
          value: purchaseOrder.notes || "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "status",
          value: purchaseOrder.status,
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "order_date",
          value: purchaseOrder.order_date
            ? purchaseOrder.order_date.split("T")[0]
            : "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "expected_delivery_date",
          value: purchaseOrder.expected_delivery_date
            ? purchaseOrder.expected_delivery_date.split("T")[0]
            : "",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "currency",
          value: purchaseOrder.currency || "USD",
        });
        dispatch({
          type: FORM_ACTIONS.SET_FIELD,
          field: "purchase_order_items",
          value: purchaseOrder.purchase_order_items.map((item) => ({
            id: item.id,
            product_id: item.product_id,
            quantity_ordered: item.quantity_ordered,
            unit_price: item.unit_price ? Number(item.unit_price) : 0,
            notes: item.notes || "",
          })),
        });
      }
    }
  }, [context, selectedId, purchaseOrders]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: name as keyof PurchaseOrderInitialState,
      value,
    });
    setTouch(true);
  };

  const handleSupplierChange = (supplierId: string) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "supplier_id",
      value: supplierId,
    });
    setTouch(true);
  };

  const handleAddPurchaseOrderItem = () => {
    const newItem: PurchaseOrderItemForm = {
      id: `temp-${Date.now()}`,
      product_id: "",
      quantity_ordered: 1,
      unit_price: 0,
      notes: "",
    };

    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "purchase_order_items",
      value: [...(state.purchase_order_items || []), newItem],
    });
  };

  const handleRemovePurchaseOrderItem = (itemId: string) => {
    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "purchase_order_items",
      value:
        state.purchase_order_items?.filter((item) => item.id !== itemId) || [],
    });
  };

  const handlePurchaseOrderItemChange = (
    itemId: string,
    field: keyof PurchaseOrderItemForm,
    value: PurchaseOrderItemForm[keyof PurchaseOrderItemForm]
  ) => {
    const updatedItems =
      state.purchase_order_items?.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      ) || [];

    dispatch({
      type: FORM_ACTIONS.SET_FIELD,
      field: "purchase_order_items",
      value: updatedItems,
    });
  };

  const handleFormVerification = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    const errors = {} as Record<string, string>;

    if (!state.supplier_id) {
      errors.supplier_id = "Supplier is required";
    }

    if (
      !state.purchase_order_items ||
      state.purchase_order_items.length === 0
    ) {
      errors.purchase_order_items = "At least one item is required";
    } else {
      for (const [index, item] of state.purchase_order_items.entries()) {
        if (!item.product_id) {
          errors[`po_item_${index}_product`] = "Product is required";
        }
        if (!item.quantity_ordered || item.quantity_ordered <= 0) {
          errors[`po_item_${index}_quantity`] =
            "Quantity must be greater than 0";
        }
        if (item.unit_price < 0) {
          errors[`po_item_${index}_unit_price`] =
            "Unit price cannot be negative";
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      dispatch({ type: FORM_ACTIONS.SET_ERRORS, errors });
      onErrorShow("Please fix the errors before submitting");
      return;
    }
    return true;
  };

  const handleSubmit = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    try {
      const validForm = handleFormVerification(e);

      if (!validForm) return;

      const {
        supplier_id,
        notes,
        status,
        purchase_order_items,
        order_date,
        expected_delivery_date,
        currency,
      } = state;

      if (context === DRAWER_CONTEXT.EDIT_PURCHASE_ORDER && selectedId) {
        // **Update existing purchase order**
        const existingPO = purchaseOrders.find((po) => po.id === selectedId);
        await usePurchaseOrdersStore.getState().updatePurchaseOrder({
          id: selectedId,
          po_number: existingPO?.po_number || "",
          supplier_id: supplier_id!,
          notes: notes || "",
          status: status!,
          order_date: order_date ? new Date(order_date).toISOString() : null,
          expected_delivery_date: expected_delivery_date
            ? new Date(expected_delivery_date).toISOString()
            : null,
          currency: currency || "USD",
          organization_id: baseUser!.organization_id!,
          created_by: baseUser!.id,
          created_at: existingPO?.created_at || new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

        // Get the original PO to compare items
        const originalPO = purchaseOrders.find((po) => po.id === selectedId);
        const originalItemIds =
          originalPO?.purchase_order_items?.map((item) => item.id) || [];
        const currentItemIds =
          purchase_order_items?.map((item) => item.id) || [];

        // Find items that were deleted
        const deletedItemIds = originalItemIds.filter(
          (id) => !currentItemIds.includes(id)
        );

        // Delete removed items
        for (const itemId of deletedItemIds) {
          await usePurchaseOrdersStore
            .getState()
            .deletePurchaseOrderItem(itemId);
        }

        // Update purchase order items
        for (const item of purchase_order_items || []) {
          if (item.id.startsWith("temp-")) {
            // Create new item
            await usePurchaseOrdersStore.getState().createPurchaseOrderItem({
              purchase_order_id: selectedId,
              product_id: item.product_id,
              quantity_ordered: item.quantity_ordered,
              quantity_received: 0,
              unit_price: item.unit_price,
              status: "pending",
              notes: item.notes || "",
              organization_id: baseUser!.organization_id!,
            });
          } else {
            // Update existing item
            await usePurchaseOrdersStore.getState().updatePurchaseOrderItem({
              id: item.id,
              purchase_order_id: selectedId,
              product_id: item.product_id,
              quantity_ordered: item.quantity_ordered,
              quantity_received:
                originalPO?.purchase_order_items?.find((i) => i.id === item.id)
                  ?.quantity_received || 0,
              unit_price: item.unit_price,
              status:
                originalPO?.purchase_order_items?.find((i) => i.id === item.id)
                  ?.status || "pending",
              notes: item.notes || "",
              organization_id: baseUser!.organization_id!,
            });
          }
        }

        // Update total
        await usePurchaseOrdersStore
          .getState()
          .updatePurchaseOrderTotal(selectedId);

        closeOnSubmit();
        onInfoShow("Purchase order updated successfully");
      } else {
        // **Create a new purchase order**
        const newPurchaseOrder = await usePurchaseOrdersStore
          .getState()
          .createPurchaseOrder({
            supplier_id: supplier_id!,
            notes: notes || "",
            status: status!,
            order_date: order_date ? new Date(order_date).toISOString() : null,
            expected_delivery_date: expected_delivery_date
              ? new Date(expected_delivery_date).toISOString()
              : null,
            currency: currency || "USD",
            created_by: baseUser!.id,
            organization_id: baseUser!.organization_id!,
          });

        // Create purchase order items
        for (const item of purchase_order_items || []) {
          await usePurchaseOrdersStore.getState().createPurchaseOrderItem({
            purchase_order_id: newPurchaseOrder.id,
            product_id: item.product_id,
            quantity_ordered: item.quantity_ordered,
            quantity_received: 0,
            unit_price: item.unit_price,
            status: "pending",
            notes: item.notes || "",
            organization_id: baseUser!.organization_id!,
          });
        }

        // Update total
        await usePurchaseOrdersStore
          .getState()
          .updatePurchaseOrderTotal(newPurchaseOrder.id);

        closeOnSubmit();
        onInfoShow("Purchase order created successfully");
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.message) {
        onErrorShow(error.message || "Failed to save purchase order");
        return;
      }
      onErrorShow("Failed to save purchase order");
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto", pb: 2 }}>
        <Section>
          <FormAutocomplete
            label="Supplier"
            value={state.supplier_id || ""}
            onChange={handleSupplierChange}
            dropdownPlaceholder="Select a supplier..."
            errorMessage={state.errors?.supplier_id}
            options={suppliers.map(
              (supplier): AutocompleteOption => ({
                label: supplier.name,
                value: supplier.id,
              })
            )}
            actionButtonText="New Supplier"
            actionButtonOnClick={() => {
              setOpenSupplierCreate(true);
            }}
          />
          <FormAutocomplete
            label="Status"
            value={state.status || "draft"}
            onChange={(value: string) => {
              dispatch({
                type: FORM_ACTIONS.SET_FIELD,
                field: "status",
                value: value as PurchaseOrderStatus,
              });
              setTouch(true);
            }}
            dropdownPlaceholder="Select status..."
            errorMessage={state.errors?.status}
            options={statusOptions}
          />
          <FormInput
            name="order_date"
            label="Order Date"
            type="date"
            value={state.order_date}
            onChange={handleChange}
            errorMessage={state.errors?.order_date}
          />
          <FormInput
            name="expected_delivery_date"
            label="Expected Delivery Date"
            type="date"
            value={state.expected_delivery_date}
            onChange={handleChange}
            errorMessage={state.errors?.expected_delivery_date}
          />
          <FormInput
            name="currency"
            label="Currency"
            value={state.currency}
            onChange={handleChange}
            errorMessage={state.errors?.currency}
            placeholder="USD"
          />
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }} color="text.secondary">
              Notes
            </Typography>
            <textarea
              name="notes"
              value={state.notes}
              onChange={(e) =>
                dispatch({
                  type: FORM_ACTIONS.SET_FIELD,
                  field: "notes",
                  value: e.target.value,
                })
              }
              placeholder="Additional notes about this purchase order"
              rows={3}
              style={{
                width: "100%",
                padding: "8px 12px",
                borderRadius: "6px",
                border: `1px solid ${
                  state.errors?.notes ? "#f3b0a2" : "#e0e0e0"
                }`,
                fontFamily: "inherit",
                fontSize: "14px",
                resize: "vertical",
                backgroundColor: state.errors?.notes
                  ? "#fffcfc"
                  : "transparent",
              }}
            />
            {state.errors?.notes && (
              <Typography variant="subtitle1" color="error" sx={{ mt: 1 }}>
                {state.errors.notes}
              </Typography>
            )}
          </Box>
        </Section>

        <Section>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h5">Items</Typography>
            <Button
              startIcon={<Add />}
              onClick={handleAddPurchaseOrderItem}
              variant="outlined"
              size="small"
            >
              Add Item
            </Button>
          </Box>

          {state.purchase_order_items?.map((item, index) => (
            <Box
              key={item.id}
              sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 2, mb: 2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="subtitle2">Item {index + 1}</Typography>
                <IconButton
                  onClick={() => handleRemovePurchaseOrderItem(item.id)}
                  size="small"
                  color="error"
                >
                  <Delete />
                </IconButton>
              </Box>

              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <FormAutocomplete
                    dropdownPlaceholder="Select a product"
                    label="Product"
                    onChange={(productId) =>
                      handlePurchaseOrderItemChange(
                        item.id,
                        "product_id",
                        productId
                      )
                    }
                    value={item.product_id}
                    errorMessage={state.errors?.[`po_item_${index}_product`]}
                    options={products.map(
                      (product): AutocompleteOption => ({
                        label: `${product.name} (${product.sku})`,
                        value: product.id,
                      })
                    )}
                  />
                </Box>

                <Box sx={{ minWidth: 120 }}>
                  <FormInput
                    name={`quantity_${item.id}`}
                    label="Quantity"
                    type="number"
                    onChange={(e) =>
                      handlePurchaseOrderItemChange(
                        item.id,
                        "quantity_ordered",
                        parseInt(e.target.value) || 0
                      )
                    }
                    value={item.quantity_ordered.toString()}
                    errorMessage={state.errors?.[`po_item_${index}_quantity}`]}
                  />
                </Box>

                <Box sx={{ minWidth: 120 }}>
                  <FormInput
                    name={`unit_price_${item.id}`}
                    label="Unit Price"
                    type="number"
                    step="0.01"
                    onChange={(e) =>
                      handlePurchaseOrderItemChange(
                        item.id,
                        "unit_price",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    value={item.unit_price.toString()}
                    errorMessage={state.errors?.[`po_item_${index}_unit_price`]}
                  />
                </Box>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ mb: 1 }}
                  color="text.secondary"
                >
                  Item Notes
                </Typography>
                <textarea
                  name={`notes_${item.id}`}
                  value={item.notes || ""}
                  onChange={(e) =>
                    handlePurchaseOrderItemChange(
                      item.id,
                      "notes",
                      e.target.value
                    )
                  }
                  placeholder="Optional notes for this item"
                  rows={2}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #e0e0e0",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    resize: "vertical",
                  }}
                />
              </Box>
            </Box>
          ))}

          {(!state.purchase_order_items ||
            state.purchase_order_items.length === 0) && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No items added. Click "Add Item" to add one.
            </Typography>
          )}
        </Section>
      </Box>

      <Footer handleClose={handleClose} handleSubmit={handleSubmit} />
      <CreateSupplierDrawer
        onClose={() => {
          setOpenSupplierCreate(false);
        }}
        open={openSupplierCreate}
        onSupplierCreated={(supplierId) => {
          // Refresh suppliers list
          if (baseUser?.organization_id) {
            getSuppliersByOrganizationId(baseUser.organization_id, false).then(
              () => {
                // Auto-select the newly created supplier
                dispatch({
                  type: FORM_ACTIONS.SET_FIELD,
                  field: "supplier_id",
                  value: supplierId,
                });
                setOpenSupplierCreate(false);
              }
            );
          }
        }}
      />
    </Box>
  );
};
